export type EventType =
  | 'MODEL_VERSION_DEPLOYED'
  | 'AI_SYSTEM_CREATED'
  | 'AI_SYSTEM_UPDATED'
  | 'AI_SYSTEM_DELETED';

export type Severity = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';

export type ChangeImpact = 'MINOR' | 'MODERATE' | 'MAJOR' | 'CRITICAL';

export type Action = 'CREATE' | 'UPDATE' | 'DELETE' | 'READ';

export type Resource = 'AI_SYSTEM' | 'MODEL' | 'USER' | 'CONFIGURATION';

export interface SystemLoggingEvent {
  id: string;
  aiSystemId: string;
  eventType: EventType | string;
  severity: Severity;
  changeImpact: ChangeImpact | null;
  message: string;
  metadata?: Record<string, any> | null;
  userId?: string | null;
  githubUserName?: string | null;
  action?: Action | string | null;
  resource?: Resource | string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  createdAt: string;
  user?: AssignedUser | null;
  repositoryName?: string | null;
}

export interface SystemLoggingEventsResponse {
  items: SystemLoggingEvent[];
  total: number;
}

export interface TrendsDataPoint {
  month: string;
  byType: Record<string, number>;
}

export interface TrendsResponse {
  period: string;
  data: TrendsDataPoint[];
}

export interface EventTypeInfo {
  code: string;
  name: string;
  metadataStructure: Record<string, string>;
}

export interface EventTypeCategory {
  category: string;
  description: string;
  eventTypes: EventTypeInfo[];
}

export interface EventTypesResponse {
  categories: EventTypeCategory[];
}

export interface SystemLoggingStats {
  totalEvents: number;
  bySeverity: Record<Severity, number>;
  byImpact: Record<ChangeImpact, number>;
  // The piechart's data source - one slice per log entry type (the same
  // `type` category used by the "Type" filter on the Audit Log pages), not
  // the narrower per-event `eventType` (which only sync events ever have).
  byType: Partial<Record<LogEntryType, number>>;
}

export interface AssignedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string | null;
  jobTitle: string | null;
}

// 'audit-organization' and 'global' are only ever returned by GET
// /system-logging/audit-logs (the org-wide "complete audit log" endpoint) -
// GET /system-logging/events stays scoped to 'audit-ai-system' | 'repo-sync'.
export type LogEntryType =
  | 'audit-ai-system'
  | 'audit-organization'
  | 'global'
  | 'repo-sync';

// Shared between the org-wide Audit Log page's "Type" filter and the
// per-AI-system page's equivalent (a subset - 'audit-organization'/'global'
// never apply to a single AI system) - keeps the wording identical rather
// than letting the two pages drift apart.
export const LOG_ENTRY_TYPE_LABELS: Record<LogEntryType, string> = {
  'audit-ai-system': 'AI System Activity',
  'audit-organization': 'Organization Activity',
  global: 'Global (Platform-wide)',
  'repo-sync': 'Repo Sync',
};

export interface AuditAiSystemDetails {
  description: string;
  fieldChanged: string[];
  oldValue?: unknown;
  newValue?: unknown;
}

export interface RepoSyncDetails {
  severity: 'Low' | 'Moderate' | 'High';
  description: string;
  repository: string | null;
  filesAdded: number;
  filesModified: number;
  filesDeleted: number;
  commit: string | null;
  uiLabel: string | null;
  uiMessage: string | null;
  userAction: string | null;
  annexIVSections: string[] | null;
  umbrellaDocuments: string[] | null;
}

export interface LogEntry {
  id: string;
  date: string;
  type: LogEntryType;
  action: string;
  user: string;
  aiSystemId: string | null;
  aiSystem: string | null;
  // Only ever populated for 'audit-ai-system' entries sourced from
  // GET /system-logging/events - the org-wide audit-logs endpoint does not
  // track per-event severity/changeImpact.
  severity?: Severity | null;
  changeImpact?: ChangeImpact | null;
  details?: AuditAiSystemDetails | RepoSyncDetails;
}

export interface LogEntryList {
  items: LogEntry[];
  total: number;
}

export function isRepoSyncLogEntry(
  entry: LogEntry
): entry is LogEntry & { details?: RepoSyncDetails } {
  return entry.type === 'repo-sync';
}

// Backs the "User" filter on both GET /events and GET /audit-logs - platform
// users (matched by id) and GitHub usernames (matched by name) are two
// distinct actor identities and are never merged into one list server-side.
export interface LogUserOptions {
  appUsers: { id: string; name: string }[];
  repoUsers: string[];
}
